import { CATEGORIES, DIFFICULTY_RANGES, type Difficulty, type Operation, type Question, type SelectedCategory } from "./types";

export function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

const SYMBOLS: Record<Operation, string> = {
  add: "+",
  sub: "−",
  mul: "×",
  div: "÷",
};

export function generateOptions(
  op: Operation,
  a: number,
  b: number,
  answer: number,
  count = 4,
): number[] {
  const pool = new Set<number>();
  const add = (v: number) => {
    if (Number.isInteger(v) && v > 0 && v !== answer && pool.size < 12) pool.add(v);
  };

  // Operation-specific mistakes: swap a factor (e.g. 7×6 → 6×6, 8×6, 7×5, 7×7)
  if (op === "mul") {
    add((a + 1) * b);
    add((a - 1) * b);
    add(a * (b + 1));
    add(a * (b - 1));
  }

  // Numeric neighbors scaled to the size of the answer
  const offsets =
    answer < 10 ? [1, 2, 3, 5] :
    answer < 30 ? [1, 2, 5, 10] :
    answer < 100 ? [1, 2, 5, 10, 20] :
    [1, 5, 10, 20, 50];
  for (const d of offsets) {
    add(answer + d);
    add(answer - d);
  }

  const poolArr = [...pool];
  for (let i = poolArr.length - 1; i > 0; i--) {
    const j = randInt(0, i);
    const temp = poolArr[i]!;
    poolArr[i] = poolArr[j]!;
    poolArr[j] = temp;
  }

  const options = [answer];
  for (const v of poolArr) {
    if (options.length >= count) break;
    options.push(v);
  }
  for (let i = options.length - 1; i > 0; i--) {
    const j = randInt(0, i);
    const temp = options[i]!;
    options[i] = options[j]!;
    options[j] = temp;
  }
  return options;
}

export function isSameQuestion(q1: Question, q2: Question): boolean {
  return q1.op === q2.op && q1.a === q2.a && q1.b === q2.b;
}

export function generateQuestion(selected: SelectedCategory, prev?: Question): Question {
  const category = CATEGORIES.find(c => c.id === selected.categoryId) ?? CATEGORIES[0]!;
  let attempts = 0;
  let q: Question;
  do {
    const op = category.operations[randInt(0, category.operations.length - 1)]!;
    const [lo, hi] = DIFFICULTY_RANGES[op][selected.difficulty];
    switch (op) {
      case "add": {
        const a = randInt(lo, hi);
        const b = randInt(lo, hi);
        q = { a, b, op: "add", answer: a + b, options: generateOptions("add", a, b, a + b) };
        break;
      }
      case "sub": {
        const b = randInt(lo, hi);
        const answer = randInt(lo, hi);
        q = { a: b + answer, b, op: "sub", answer, options: generateOptions("sub", b, answer, answer) };
        break;
      }
      case "mul": {
        const a = randInt(lo, hi);
        const b = randInt(lo, hi);
        q = { a, b, op: "mul", answer: a * b, options: generateOptions("mul", a, b, a * b) };
        break;
      }
      case "div": {
        const b = randInt(lo, hi);
        const answer = randInt(lo, hi);
        q = { a: b * answer, b, op: "div", answer, options: generateOptions("div", b, answer, answer) };
        break;
      }
    }
    attempts++;
  } while (prev && isSameQuestion(q, prev) && attempts < 30);

  return q;
}

export function generateQuestionSet(selected: SelectedCategory, count: number): Question[] {
  const list: Question[] = [];
  for (let i = 0; i < count; i++) {
    const prev = list[list.length - 1];
    list.push(generateQuestion(selected, prev));
  }
  return list;
}

export function formatQuestion(q: Question): string {
  return `${q.a} ${SYMBOLS[q.op]} ${q.b}`;
}

export function rangeLabel(op: Operation, difficulty: Difficulty): string {
  const [lo, hi] = DIFFICULTY_RANGES[op][difficulty];
  const sign = op === "add" ? "+" : op === "sub" ? "−" : "";
  return `${sign}${lo} ~ ${sign}${hi}`;
}
